/**
 * Routing an inbox item INTO an existing container.
 *
 * `inboxRouting.ts` creates standalone entities; this creates nested ones. The
 * split matters because the nested types (goal / skill / requirement) have no
 * standalone form at all — a Goal only exists inside a Quest — so they are only
 * reachable once the user has picked where they belong.
 */
import { useTaskStore } from '../stores/taskStore';
import { useFolderStore } from '../stores/folderStore';
import { useProjectStore } from '../stores/projectStore';
import { useQuestStore } from '../stores/questStore';
import { useAbilitiesStore } from '../stores/abilitiesStore';
import type { Task, ProjectTask } from '../types';
import type { InboxDestination, InboxTarget, InboxTargetType } from '../types/inbox';

export interface AttachInput {
  title: string;
  description?: string;
  category: string;
  /** yyyy-MM-dd, when the user picked a date at the Route step. */
  scheduledFor?: string;
}

/** One option in the "Attach to" picker. */
export interface AttachOption {
  type: InboxTargetType;
  id: string;
  label: string;
  /** Set for nested targets — a skill's owning ability. */
  parentId?: string;
  parentLabel?: string;
}

/**
 * Everything of `type` the user could attach to, read live from the stores.
 * Returned flat and pre-labelled so the picker needs no store knowledge.
 */
export function attachOptions(type: InboxTargetType): AttachOption[] {
  switch (type) {
    case 'project':
      return useProjectStore.getState().projects
        .filter(p => p.status !== 'archived')
        .map(p => ({ type, id: p.id, label: p.title }));

    case 'taskFolder':
      // Task folders live in folderStore (taskStore only carries the folderId).
      return useFolderStore.getState().folders.map(f => ({ type, id: f.id, label: f.name }));

    case 'quest':
      return useQuestStore.getState().quests
        .filter(q => q.status !== 'archived')
        .map(q => ({ type, id: q.id, label: q.title }));

    case 'ability':
      return useAbilitiesStore.getState().abilities.map(a => ({ type, id: a.id, label: a.name }));

    case 'skill':
      // Two levels: a requirement needs BOTH the skill and its owning ability, so
      // the ability is carried along rather than looked up again at route time.
      return useAbilitiesStore.getState().abilities.flatMap(a =>
        (a.skills ?? []).map(sk => ({
          type, id: sk.id, label: sk.name, parentId: a.id, parentLabel: a.name,
        }))
      );

    default:
      return [];
  }
}

/**
 * Create the nested entity. Returns its id, or undefined when the combination
 * isn't one we attach (the caller then falls back to standalone routing).
 */
export function attachInboxItem(
  destination: InboxDestination,
  target: InboxTarget,
  { title, description, category, scheduledFor }: AttachInput
): string | undefined {
  const now = new Date().toISOString();

  // ── a Task, into a Project's first phase ────────────────────────────────
  if (destination === 'task' && target.type === 'project') {
    const project = useProjectStore.getState().projects.find(p => p.id === target.id);
    if (!project) return undefined;
    // ProjectTask requires a phaseId; new projects are seeded with DEFAULT_PHASES.
    const phaseId = project.phases?.[0]?.id;
    if (!phaseId) return undefined;
    // ⚠️ No `as any` here. An `as any` on a store action is exactly what hid the
    // missing `id` that made a task its own child and blew the stack (see
    // utils/taskTree.ts). If this stops compiling, the shape is wrong — fix it.
    const projectTask: Omit<ProjectTask, 'id' | 'order'> = {
      title,
      ...(description ? { description } : {}),
      completed: false,
      status: 'todo',
      priority: 'medium',
      phaseId,
      ...(scheduledFor ? { dueDate: scheduledFor } : {}),
    };
    useProjectStore.getState().addTask(project.id, projectTask);
    return project.id;
  }

  // ── a Task, into a task Folder ──────────────────────────────────────────
  if (destination === 'task' && target.type === 'taskFolder') {
    // ⚠️ taskStore.addTask takes a COMPLETE Task and generates NO id. Omitting it
    // makes the task its own parent and blows the stack — never pass a partial.
    const task: Task = {
      id: crypto.randomUUID(),
      title,
      ...(description ? { description } : {}),
      category,
      status: scheduledFor ? 'scheduled' : 'categorized',
      completed: false,
      folderId: target.id,
      ...(scheduledFor ? { dueDate: scheduledFor } : {}),
      createdAt: now,
      updatedAt: now,
    };
    useTaskStore.getState().addTask(task);
    return task.id;
  }

  // ── a Goal, into a Quest ────────────────────────────────────────────────
  if (destination === 'goal' && target.type === 'quest') {
    // A goal is an action + an object + a count. A captured line is the object;
    // the rest gets a sane default the user can edit in the quest itself.
    return useQuestStore.getState().addGoal(target.id, {
      actionVerb: 'Complete',
      what: title,
      quantity: 1,
      ...(scheduledFor ? { byWhen: scheduledFor } : {}),
    });
  }

  // ── a Skill, into an Ability ────────────────────────────────────────────
  if (destination === 'skill' && target.type === 'ability') {
    return useAbilitiesStore.getState().addSkill(target.id, {
      name: title,
      description: description ?? '',
      level: 'foundation',
      requirements: [],
    });
  }

  // ── a Requirement, into a Skill (which lives in an Ability) ─────────────
  if (destination === 'requirement' && target.type === 'skill') {
    if (!target.parentId) return undefined;
    return useAbilitiesStore.getState().addRequirement(target.parentId, target.id, {
      title,
      ...(description ? { description } : {}),
      type: 'practice',
      progress: 0,
      requirementType: 'input',
    });
  }

  return undefined;
}
